// The school dashboard shell, ported from Dashboard.dc.html (Ship C scope:
// the shell, tiles, requirements panel, portal-link view, and the EMPTY
// state ship now; roster/activity/attention DATA and their actions land as
// Ship C wires school auth and the roster store, and none of the comp's demo
// roster rows ship here because a real dashboard must not render invented
// pilots). Reached at <slug>.clearedto.rent/dashboard.
import { useEffect, useState } from 'react';
import { api } from '@/lib/api.js';
import { authApi, sessionToken, clearSession, dashboardUrlFor } from '@/lib/auth.js';
import Modal from '@/components/ui/Modal.jsx';
import StatusPill from '@/components/ui/StatusPill.jsx';
import Team from '@/routes/dashboard/Team.jsx';
import planeUrl from '@/assets/paper-plane.svg';

const NAV = [
  ['roster', 'Covered roster'],
  ['attention', 'Needs attention'],
  ['reqs', 'Requirements'],
  ['team', 'Team'],
  ['portal', 'Portal link & QR'],
  ['audit', 'Audit export'],
];

const money = (v) => `$${Number(v || 0).toLocaleString('en-US')}`;
const monogram = (name) => String(name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');

const EMPTY_TILES = [
  ['Cleared to rent', 'via portal or outside COIs'],
  ['CFIs covered', 'instructor non-owned'],
  ['Expiring in 30 days', 'we watch this for you'],
  ['Coverage gaps', 'flagged the day they happen'],
];

/** One row's pill: the colour never carries the meaning without the word. */
// The roster row's pill: the SERVER's derived word and look (utils/ctr-renter-state.js, the one derivation; Cleared needs
// Global's bind status AND a binder or policy listed). This page derives nothing from status or compliance.
function rosterPill(r) {
  return [r.tone || 'grey', r.label || 'Undetermined'];
}

function ViewTitle({ title, sub }) {
  return (
    <div className="mb-6">
      <h1 className="text-[22px] font-bold tracking-[-0.01em]">{title}</h1>
      <div className="mt-1 text-[13px] text-ink-dim">{sub}</div>
    </div>
  );
}

export default function Dashboard({ slug }) {
  const [school, setSchool] = useState(undefined);
  const [licensedStates, setLicensedStates] = useState(null);
  const [view, setView] = useState('roster');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteNote, setInviteNote] = useState(false);
  const [copied, setCopied] = useState(false);
  // me: undefined = checking, null = signed out, {email, schools} = signed in
  const [me, setMe] = useState(undefined);
  const [roster, setRoster] = useState(null);
  const [rosterCounts, setRosterCounts] = useState(null);

  useEffect(() => {
    api.get(`/api/ctr/schools/by-slug/${encodeURIComponent(slug)}`)
      .then((r) => setSchool(r.school)).catch(() => setSchool(null));
    api.get('/api/ctr/config').then((r) => setLicensedStates(r.licensed_states ?? null)).catch(() => {});
    if (sessionToken()) {
      authApi.get('/api/ctr/me').then(setMe).catch(() => { setMe(null); });
    } else { setMe(null); }
  }, [slug]);

  const myRole = me?.schools?.find((s) => s.slug === slug)?.role || null;
  useEffect(() => {
    if (!myRole) { setRoster(null); return; }
    authApi.get(`/api/ctr/schools/${encodeURIComponent(slug)}/roster`)
      .then((r) => { setRoster(r.roster || []); setRosterCounts(r.summary || null); }).catch(() => { setRoster(null); setRosterCounts(null); });
  }, [slug, myRole]);

  if (school === undefined) return <div className="p-10 text-center text-sm text-ink-dim">Loading...</div>;
  if (school === null) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-ink-dim">
        No school portal lives at this address yet.
      </div>
    );
  }

  const portalUrl = `https://${school.slug}.clearedto.rent`;
  const req = school.requirements || {};
  const unlicensed = Array.isArray(licensedStates) && school.home_state && !licensedStates.includes(school.home_state);
  const reqRows = [
    ['Liability (each occurrence / pax)', req.liability ? `${money(req.liability.limit)}${req.liability.excluding_passengers ? ' excl. pax' : ` / ${money(req.liability.pax_sublimit)}`}` : '', ''],
    ['Damage to rented aircraft', money(req.damage_limit?.limit), ''],
    ['Deductible reimbursement', Number(req.deductible_reimb?.limit) > 0 ? money(req.deductible_reimb.limit) : 'Not required', ''],
    ['Medical expense', money(req.medical?.limit), '· included'],
    ['Certificate holder', school.legal_name, '· locked'],
  ];

  function copyLink() {
    navigator.clipboard?.writeText(portalUrl).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  }

  return (
    <div className="mx-auto grid min-h-screen max-w-[1400px] shadow-shell md:grid-cols-[236px_1fr]">
      <aside className="flex flex-col bg-primary-deep py-[22px] text-teal-mute-dark">
        <div className="mb-3.5 flex items-center gap-3 border-b border-white/10 px-5 pb-5">
          {school.logo_path
            ? <img src={school.logo_path} alt="" className="h-[38px] w-[38px] flex-none rounded-[9px] bg-white object-contain" />
            : <div className="flex h-[38px] w-[38px] flex-none items-center justify-center rounded-[9px] bg-white font-bold text-primary">{monogram(school.name)}</div>}
          <div>
            <div className="text-[15px] font-bold leading-[1.3] text-white">{school.name}</div>
            {school.home_airport ? (
              <div className="mt-[3px] text-[11px] opacity-70">
                {school.home_airport}{school.home_airport_name ? ` · ${school.home_airport_name}` : ''}
              </div>
            ) : null}
          </div>
        </div>
        {school.watermark ? (
          <div title={`Your school is verified automatically the first time a policy binds naming ${school.legal_name}. Nothing for you to do.`}
            className="mx-5 mb-3 cursor-help rounded-[9px] border border-warn-line/40 bg-warn-line/15 p-[8px_11px] text-[11px] leading-[1.5] text-warn-line">
            <b>Verifying</b> · clears when your first policy binds
          </div>
        ) : null}
        {unlicensed ? (
          <div title="We can't yet quote new policies in your state (producer licensing in progress). Outside COIs are tracked as usual."
            className="mx-5 mb-3 cursor-help rounded-[9px] border border-white/15 bg-white/5 p-[8px_11px] text-[11px] leading-[1.5]">
            Students here can be tracked, not yet insured through the portal
          </div>
        ) : null}
        <nav className="flex flex-col">
          {NAV.map(([id, label]) => (
            <button key={id} type="button" onClick={() => setView(id)}
              className={`flex w-full items-center gap-2.5 border-l-[3px] px-5 py-3 text-left text-[13.5px] font-semibold hover:text-white ${view === id ? 'border-sky bg-white/10 text-white' : 'border-transparent'}`}>
              <span className="flex-1">{label}</span>
            </button>
          ))}
        </nav>
        <div className="mt-auto flex flex-col gap-3 px-5 pt-[18px]">
          {me ? (
            <div className="flex flex-col gap-2">
              {me.schools.length > 1 ? (
                <select
                  aria-label="Switch school"
                  value={slug}
                  onChange={(e) => { const s = e.target.value; if (s !== slug) window.location.href = dashboardUrlFor(s); }}
                  className="w-full rounded-[9px] border border-white/20 bg-white/10 p-[8px_10px] text-[12.5px] font-semibold text-white outline-none">
                  {me.schools.map((s) => <option key={s.slug} value={s.slug} className="text-ink">{s.name}</option>)}
                </select>
              ) : null}
              <div className="truncate text-[11px] opacity-70">{me.email}</div>
              <button type="button" onClick={() => { clearSession(); window.location.href = '/signin?signedout=1'; }}
                className="w-full rounded-[9px] border border-white/20 bg-white/5 py-[8px] text-[12.5px] font-semibold text-white hover:bg-white/10">
                Sign out
              </button>
            </div>
          ) : me === null ? (
            <a href="/signin" className="block w-full rounded-[9px] bg-white/10 py-[9px] text-center text-[12.5px] font-semibold text-white hover:bg-white/20">
              Sign in
            </a>
          ) : null}
          <div className="text-[10.5px] leading-[1.6] opacity-70">
            Powered by clearedto.rent<br />a service of NDEMNIFLY Aviation Insurance<br />CA License #6018700
          </div>
        </div>
      </aside>

      <main className="min-w-0 bg-paper p-7 lg:px-8">
        {view === 'roster' ? (
          <>
            <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
              <div>
                <h1 className="text-[22px] font-bold tracking-[-0.01em]">Covered roster</h1>
              </div>
              <div className="flex gap-2.5">
                <button type="button" onClick={() => setInviteOpen(true)}
                  className="rounded-card bg-primary px-[18px] py-[11px] text-[13.5px] font-semibold text-white hover:bg-primary-deep">
                  Invite a student
                </button>
              </div>
            </div>
            {me === null ? (
              <div className="mb-[18px] flex flex-wrap items-center justify-between gap-3 rounded-card-lg border border-teal-line bg-teal-faint p-[13px_18px]">
                <span className="text-[13px] leading-[1.5] text-teal-mute">
                  <b>Sign in to see your live roster.</b> The link comes by email; no passwords here.
                </span>
                <a href="/signin" className="flex-none rounded-[9px] bg-primary px-4 py-2 text-[12.5px] font-semibold text-white hover:bg-primary-deep">Sign in</a>
              </div>
            ) : null}
            <div className="mb-[22px] grid grid-cols-2 gap-3.5 lg:grid-cols-4">
              {(() => {
                const c = rosterCounts || {};
                const cleared = c.cleared || 0;
                const cfis = c.cfis || 0;
                const soon = c.expiring_soon || 0;
                const gaps = c.gaps || 0;
                const counts = [cleared, cfis, soon, gaps];
                return EMPTY_TILES.map(([label, sub], i) => (
                  <div key={label} className="rounded-card-lg border border-line bg-white p-[18px_20px]">
                    <div className="text-[11.5px] font-bold uppercase tracking-[0.05em] text-ink-dim">{label}</div>
                    <div className={`mt-1.5 text-[32px] font-bold tracking-[-0.02em] ${counts[i] ? 'text-ink' : 'text-line-strong'}`}>{counts[i]}</div>
                    <div className="mt-[3px] text-xs text-ink-faint">{sub}</div>
                  </div>
                ));
              })()}
            </div>
            {roster && roster.length ? (
              <div className="overflow-hidden rounded-panel border border-line bg-white">
                {roster.map((r) => (
                  <div key={r.id + (r.shared ? '-shared' : '')} className="flex items-center justify-between gap-3 border-b border-fill p-[13px_20px] last:border-0">
                    <div className="min-w-0">
                      <div className="text-[13.5px] font-semibold">{[r.pilot_first, r.pilot_last].filter(Boolean).join(' ') || r.pilot_email}</div>
                      <div className="text-[11.5px] text-ink-faint">
                        {r.is_cfi ? 'CFI · ' : ''}
                        {r.shared ? 'shared from another school' : r.source === 'outside' ? 'outside policy' : r.source === 'direct' ? 'direct quote, attributed here' : 'via portal'}
                        {r.expiration_date ? ` · renews ${new Date(r.expiration_date).toLocaleDateString()}` : ''}
                      </div>
                    </div>
                    <StatusPill status={rosterPill(r)[0]}>{rosterPill(r)[1]}</StatusPill>
                  </div>
                ))}
              </div>
            ) : (
            <div className="rounded-panel border border-line bg-white p-[56px_40px] text-center">
              <div className="mx-auto mb-[18px] flex h-16 w-16 items-center justify-center rounded-modal bg-teal-card text-accent">
                <span className="inline-block h-[26px] w-8 bg-current"
                  style={{ maskImage: `url(${planeUrl})`, maskSize: 'contain', maskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskImage: `url(${planeUrl})`, WebkitMaskSize: 'contain', WebkitMaskRepeat: 'no-repeat', WebkitMaskPosition: 'center' }} />
              </div>
              <h2 className="mb-2 text-xl font-bold">Your roster fills itself. Give it a door.</h2>
              <p className="mx-auto mb-[26px] max-w-[440px] text-sm leading-[1.6] text-ink-dim">
                Put the poster at the front desk and the link in your enrollment packet. The first student who buys
                shows up here, cleared, about 2 minutes later.
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <button type="button" onClick={() => setView('portal')}
                  className="rounded-card bg-primary px-[22px] py-[13px] text-sm font-semibold text-white hover:bg-primary-deep">
                  Get my poster &amp; QR
                </button>
                <button type="button" onClick={() => setInviteOpen(true)}
                  className="rounded-card border-[1.5px] border-line-strong bg-white px-5 py-[11.5px] text-sm font-semibold text-primary hover:border-primary">
                  Invite your first student
                </button>
              </div>
            </div>
            )}
          </>
        ) : null}

        {view === 'attention' ? (
          <>
            <ViewTitle title="Needs attention" sub="Only the rows that need a human. Everything else is running itself." />
            <div className="max-w-[820px] p-2.5 text-center text-[13px] text-ink-faint">
              That&apos;s everything. Nothing on your roster needs you today.
            </div>
          </>
        ) : null}

        {view === 'reqs' ? (
          <>
            <ViewTitle title="Requirements" sub="The minimum requirements on every quote your students see." />
            <div className="max-w-[680px]">
              <div className="mb-4 rounded-card-lg bg-outside-bg p-[14px_18px] text-[13px] leading-[1.6] text-outside-fg">
                <b>Raising a floor doesn&apos;t change existing policies.</b> Students already covered stay covered at
                their current limits until renewal; new purchases and renewals use the new floor. Lowering a floor
                applies right away.
              </div>
              <div className="rounded-panel border border-line bg-white p-[26px_28px]">
                <div className="mb-1.5 flex items-center justify-between gap-4">
                  <span className="text-[13px] font-bold text-primary">Derived from your fleet</span>
                </div>
                {reqRows.map(([k, v, note]) => (
                  <div key={k} className="flex justify-between gap-4 border-b border-fill py-2.5 text-[13.5px] last:border-0">
                    <span className="text-ink-dim">{k}</span>
                    <span className="text-right font-semibold">{v} <span className="text-[11px] font-normal text-ink-faint">{note}</span></span>
                  </div>
                ))}
                <div className="mt-2 text-xs leading-[1.5] text-ink-faint">
                  Requirement editing arrives with your full dashboard; until then, changes go through us and take
                  effect the same day. The certificate holder is your legal name from signup.
                </div>
              </div>
            </div>
          </>
        ) : null}

        {view === 'team' ? (
          <>
            <ViewTitle title="Team" sub="Who can see and work this dashboard. Invites come with a sign-in link and expire in 7 days." />
            {me ? (
              <Team slug={slug} me={me.email} />
            ) : (
              <div className="flex max-w-[560px] flex-wrap items-center justify-between gap-3 rounded-panel border border-line bg-white p-[22px_24px]">
                <span className="text-[13.5px] leading-[1.6] text-ink-dim"><b className="text-ink">Sign in to manage your team.</b> The link comes by email; no passwords here.</span>
                <a href="/signin" className="flex-none rounded-[9px] bg-primary px-4 py-2.5 text-[13px] font-semibold text-white hover:bg-primary-deep">Sign in</a>
              </div>
            )}
          </>
        ) : null}

        {view === 'portal' ? (
          <>
            <ViewTitle title="Portal link & QR" sub="Everything that points students at your portal." />
            <div className="grid max-w-[980px] items-start gap-4 lg:grid-cols-[1.2fr_0.8fr]">
              <div className="flex flex-col gap-3.5">
                <div className="rounded-card-lg border border-line bg-white p-[22px_24px]">
                  <div className="mb-2.5 text-[13.5px] font-bold">Your portal link</div>
                  <div className="flex gap-2.5">
                    <div className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap rounded-[9px] border-[1.5px] border-line bg-paper p-[12px_14px] text-sm font-semibold text-accent">{portalUrl}</div>
                    <button type="button" onClick={copyLink}
                      className="flex-none rounded-[9px] bg-primary px-[18px] text-[13.5px] font-semibold text-white hover:bg-primary-deep">
                      {copied ? 'Copied ✓' : 'Copy link'}
                    </button>
                  </div>
                  <div className="mt-2.5 text-[12.5px] leading-[1.5] text-ink-faint">
                    Put it in your enrollment packet and rental agreement: &quot;Proof of coverage: purchase or upload here.&quot;
                  </div>
                </div>
                <div className="rounded-card-lg border border-line bg-white p-[22px_24px]">
                  <div className="mb-3.5 text-[13.5px] font-bold">Print kit</div>
                  <div className="flex flex-col gap-0.5" data-print-kit-list>
                    {[
                      ['Lobby poster · 11×17, your logo & QR', ''],
                      ['Counter sign · 8.5×11, acrylic-holder ready', '?piece=counter_8x11'],
                      ['Table tent · 4×6, dispatch counter & lounge', '?piece=tent_4x6'],
                      ['QR sticker · 2-inch, "Scan to get cleared"', '?piece=sticker_2in'],
                      ['Enrollment-packet insert · 4×6 kit card', '?piece=insert_4x6'],
                    ].map(([label, q]) => (
                      <div key={label} className="flex items-center justify-between border-b border-fill py-[11px] last:border-0">
                        <span className="text-[13.5px]">{label}</span>
                        <a className="text-[12.5px] font-semibold" href={`/api/ctr/schools/${school.slug}/poster.pdf${q}`} target="_blank" rel="noreferrer">Download PDF ↓</a>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rounded-card-lg border border-line bg-white p-[22px_24px]">
                  <div className="mb-2.5 text-[13.5px] font-bold">Embed on your website</div>
                  <div className="overflow-x-auto rounded-[9px] bg-ink p-[14px_16px] font-mono text-[11.5px] leading-[1.6] text-line-strong">
                    &lt;a href=&quot;{portalUrl}&quot;&gt;<br />
                    &nbsp;&nbsp;Get renter&apos;s insurance, cleared for {school.name}<br />
                    &lt;/a&gt;
                  </div>
                </div>
              </div>
              <div className="rounded-card-lg border border-line bg-white p-6 text-center">
                <div className="mb-4 text-[11px] font-bold uppercase tracking-[0.1em] text-ink-faint">Poster preview</div>
                <div className="rounded-lg border border-line bg-white p-[28px_22px] shadow-card">
                  <div className="mx-auto mb-3.5 flex h-11 w-11 items-center justify-center rounded-[10px] text-base font-bold text-white" style={{ background: 'var(--school-accent)' }}>{monogram(school.name)}</div>
                  <div className="mb-4 text-[17px] font-bold leading-[1.3] tracking-[-0.01em]">Renting at {school.name}?<br />Get cleared in 2 minutes.</div>
                  <div className="mx-auto mb-3.5 h-[110px] w-[110px] rounded-md opacity-85" style={{ background: 'repeating-linear-gradient(90deg, currentColor 0 5px, transparent 5px 10px), repeating-linear-gradient(0deg, currentColor 0 5px, transparent 5px 10px)' }} />
                  <div className="mb-3.5 text-xs font-bold text-accent">{school.slug}.clearedto.rent</div>
                  <div className="text-left text-[9.5px] leading-[1.6] text-ink-faint">
                    ✓ Your required coverage, preloaded<br />
                    ✓ Your certificate goes to {school.name} automatically<br />
                    ✓ Coverage by Global Aerospace, arranged by NDEMNIFLY
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : null}

        {view === 'audit' ? (
          <>
            <ViewTitle title="Audit export" sub="A CSV of your roster and its coverage history, for insurers, auditors, or your own file." />
            <div className="max-w-[640px] rounded-panel border border-line bg-white p-[26px_28px]">
              <div className="mb-5 rounded-[10px] border border-fill bg-paper p-[14px_16px] text-[12.5px] leading-[1.7] text-ink-dim">
                <b className="text-ink-soft">Included columns:</b> pilot, role, status, carrier, policy number, liability
                limit, passenger sublimit, aircraft damage limit, deductible reimbursement, effective date, expiration,
                renewal mode, certificate link, every status change with timestamp.
              </div>
              <button type="button" disabled className="cursor-not-allowed rounded-card bg-primary px-6 py-[13px] text-sm font-semibold text-white opacity-50">
                Download CSV
              </button>
              <div className="mt-3 text-xs text-ink-faint">
                Exports switch on with your roster. Timestamps are the audit trail: they show what you knew and when.
              </div>
            </div>
          </>
        ) : null}
      </main>

      <Modal open={inviteOpen} onClose={() => { setInviteOpen(false); setInviteNote(false); }} maxWidth="max-w-[480px]">
        <div className="mb-1.5 flex items-start justify-between">
          <h2 className="text-[19px] font-bold tracking-[-0.01em]">Invite a student</h2>
          <button type="button" onClick={() => { setInviteOpen(false); setInviteNote(false); }} className="text-xl leading-none text-ink-faint hover:text-ink">×</button>
        </div>
        <p className="mb-[18px] text-[13.5px] leading-[1.6] text-ink-dim">
          They&apos;ll get one email with your portal link. The portal does all the explaining; you never have to
          discuss coverage.
        </p>
        <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.04em] text-ink-soft">Student email</label>
        <input placeholder="student@example.com" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
          className="mb-4 w-full rounded-[9px] border-[1.5px] border-line p-[12px_14px] text-sm outline-none focus:border-accent" />
        {inviteNote ? (
          <div className="mb-3 rounded-card bg-teal-fill p-[10px_13px] text-[12.5px] text-primary">
            Email invitations arrive with your roster. For now, share the link below directly; it works today.
          </div>
        ) : null}
        <button type="button" onClick={() => setInviteNote(true)}
          className="w-full rounded-card bg-primary p-[13px] text-sm font-semibold text-white hover:bg-primary-deep">
          Send invite
        </button>
        <div className="mt-3.5 text-center text-xs text-ink-faint">or share the link directly: <b className="text-accent">{school.slug}.clearedto.rent</b></div>
      </Modal>
    </div>
  );
}
